import React from "react";
import { useParams } from "react-router-dom";
import algoliasearch from "algoliasearch";

import {
  Container,
  Typography,
  Avatar,
  Dialog,
  Fab,
  IconButton,
  Button,
  Slide,
  Card,
  CardContent,
  Grid,
  Chip,
  CardActions,
  CircularProgress,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import ListIcon from "@material-ui/icons/List";
import EmailIcon from "@material-ui/icons/Email";
import TwitterIcon from "@material-ui/icons/Twitter";

import { MEP } from "../Home/MEP";
import { ScoreView, normaliseMEPScore } from "../Utilities/ScoreView";

const client = algoliasearch("V7NYNJIM7C", "439a81efb8f918ee5bf3b0380b0f9f28");

const Transition = React.forwardRef(function Transition(props, ref) {
  // @ts-ignore
  return <Slide direction="up" ref={ref} {...props} />;
});

export const MEPDetails: React.FC = () => {
  const [index] = React.useState(client.initIndex("meps"));

  const { id } = useParams<{ id: string }>();
  const [mep, setMep] = React.useState<MEP>();

  const [fingerPrintOpen, setFingerPrintOpen] = React.useState(false);

  React.useEffect(() => {
    const getMep = async () => {
      setMep(await index.getObject<MEP>(id));
    };

    getMep();
  }, [index, id]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!mep)
    return (
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />;
      </div>
    );

  return (
    <div>
      <Dialog
        open={fingerPrintOpen}
        // @ts-ignore
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setFingerPrintOpen(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        maxWidth="lg"
      >
        <img
          src={`https://storage.googleapis.com/futurereadinessindex.appspot.com/fingerprints/wc_${mep.id}_large.png`}
          style={{ width: "500px", height: "500px", objectFit: "none", margin: "0 auto" }}
          alt="Political Fingerprint"
        />
        <Typography variant="caption" align="center">
          Political fingerprint of {mep.name}
        </Typography>
      </Dialog>

      <div className="top-area">
        <div className="mep-profile-card">
          <Avatar
            alt={mep.name}
            src={mep.image}
            style={{
              height: "150px",
              width: "150px",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          />
          <Typography variant="h5" gutterBottom>
            {mep.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Political party
          </Typography>
          <Typography>{mep.party}</Typography>
          <Typography variant="caption" color="textSecondary">
            Group
          </Typography>
          <Typography>{mep.group}</Typography>
          <Typography variant="caption" color="textSecondary">
            Nationality
          </Typography>
          <Typography>{mep.nationality}</Typography>
          <div style={{ marginTop: "20px", marginBottom: "20px" }}>
            <Fab
              color="secondary"
              aria-label="political-fingerprint"
              onClick={() => setFingerPrintOpen(true)}
            >
              <FingerprintIcon />
            </Fab>
          </div>

          <Grid container alignItems="center" justify="center">
            {mep.mail && (
              <IconButton target="_blank" href={`mailto:${mep.mail}`}>
                <EmailIcon />
              </IconButton>
            )}
            {mep.twitter && (
              <IconButton target="_blank" href={mep.twitter}>
                <TwitterIcon />
              </IconButton>
            )}
          </Grid>
        </div>
        <div style={{ padding: "20px" }}>
          <ScoreView
            score={mep.score}
            scoreNormalised={normaliseMEPScore(mep.score)}
          />
        </div>
      </div>

      <Container maxWidth={false}>
        <Grid container alignItems="center" wrap="nowrap">
          <Grid item style={{ marginRight: "15px" }}>
            <ListIcon />
          </Grid>
          <Grid item>
            <Typography
              variant="h6"
              style={{ marginTop: "20px", marginBottom: "20px" }}
            >
              {mep.name} has contributed amendments to the following activities
            </Typography>
          </Grid>
        </Grid>

        {mep.activities
          .sort((a, b) => {
            if (a.score > b.score) {
              return -1;
            }
            if (a.score < b.score) {
              return 1;
            }
            // a must be equal to b
            return 0;
          })
          .map((activity) => (
            <Card style={{ marginBottom: "20px", width: "100%" }}>
              <CardContent>
                <Grid container direction="row">
                  <Grid item xs={true}>
                    <Typography variant="caption" color="textSecondary">
                      Activity
                    </Typography>
                    <Typography>{activity.title}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Contribution
                    </Typography>
                    <Typography>
                      {mep.name} contributed with {activity.amendments}{" "}
                      {activity.amendments > 1 ? "amendments " : "amendment "}
                      to this activity. Multiplying {activity.amendments} with
                      the highest score for a related subject results in a score
                      of{" "}
                      <Chip
                        size="small"
                        color="primary"
                        label={activity.score}
                      />
                      .
                    </Typography>
                    <div>
                      <Typography variant="caption" color="textSecondary">
                        Related subjects
                      </Typography>
                      <Table size="small" aria-label="a dense table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell align="right">Score</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activity.subjects
                            .sort((a, b) => {
                              if (a.score > b.score) {
                                return -1;
                              }
                              if (a.score < b.score) {
                                return 1;
                              }
                              // a must be equal to b
                              return 0;
                            })
                            .map((subject) => (
                              <TableRow key={subject.title}>
                                <TableCell component="th" scope="row">
                                  {subject.title}
                                </TableCell>
                                <TableCell align="right">
                                  {subject.score}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Grid container justify="flex-end">
                  <Grid item>
                    <Button
                      target="_blank"
                      href={`mailto:${mep.mail ? mep.mail : ""}?subject=${activity.title
                        }`}
                      style={{ marginRight: "10px" }}
                      size="small"
                      variant="outlined"
                      color="primary"
                    >
                      Reach out
                    </Button>
                    <Button
                      target="_blank"
                      href={`https://parltrack.org/activities/${mep.id}/dossier/${activity.epref}`}
                      style={{ marginRight: "10px" }}
                      size="small"
                      variant="outlined"
                      color="primary"
                    >
                      Explore contributions
                    </Button>
                  </Grid>
                </Grid>
              </CardActions>
            </Card>
          ))}
      </Container>
    </div>
  );
};
